import { FC, Suspense, useEffect, useRef } from "react";
import { graphql, useFragment } from "react-relay";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Header as BaseHeader,
  PrivateModeToggle,
  ProviderIcon,
  useGlobalModalContext,
  useLocale,
} from "rujira.ui";
import "rujira.ui/scss/index.scss";
import privateAccountIcon from "rujira.ui/assets/images/ghost-wallet.svg";

import { Link } from "../../Gate";
import {
  HeaderAccountFragment$data,
  HeaderAccountFragment$key,
} from "./__generated__/HeaderAccountFragment.graphql";
import { isUnclaimedMerge } from "../../portfolio/utils";
import { usePortfolioBalanceValue } from "../../portfolio/usePortfolioBalanceValue";
import { usePreloadedAccountData } from "../../services/accountData";
import { useWalletAccounts } from "../../services/accounts";
import { parseBanxaDeepLink } from "../../services/banxaDeepLink";
import { usePreloadedPendingDeposits } from "../../services/deposits";
import { BanxaOnRamp } from "./BanxaOnRamp";
import { Deposit } from "./Deposit";
import { isModelBEnabled } from "../../redacted/v2/rotation";
import { BannerContent, shouldShowBanner } from "../Banner";
import { useRedacted } from "../../services/redacted";
import { usePrivacyProvider } from "../../redacted/provider";
import { useRedactedV2 } from "../../redacted/v2";
import { privateHeaderOperation } from "./privateHeaderOperation";

import { WALLETS } from "../wallets";

export const Header: FC = () => {
  return (
    <Suspense fallback={<HeaderLoading />}>
      <HeaderContent />
    </Suspense>
  );
};

export const HeaderAccountFragment = graphql`
  fragment HeaderAccountFragment on Account {
    fin {
      ordersV2(first: 100) {
        edges {
          node {
            __typename
            ... on FinOrder {
              valueUsd
            }
            ... on CalcOrder {
              valueUsd
            }
          }
        }
      }
      ranges(first: 100) {
        edges {
          node {
            valueUsd
          }
        }
      }
    }
    merge {
      accounts {
        shares
        valueUsd
      }
    }
    credit {
      accounts {
        id
        valueUsd
      }
    }
    strategies {
      __typename
      ... on ThorchainLiquidityProvider {
        id
        valueUsd
      }
      ... on BowAccount {
        id
        valueUsd
      }
      ... on StakingAccount {
        id
        valueUsd
      }
      ... on IndexAccount {
        id
        sharesValue
      }
      ... on GhostVaultAccount {
        id
        valueUsd
      }
    }
  }
`;

export const headerAccountValue = (
  account: HeaderAccountFragment$data
): bigint =>
  (account.fin?.ordersV2?.edges || []).reduce(
    (total, edge) =>
      total +
      BigInt(edge?.node && "valueUsd" in edge.node ? edge.node.valueUsd : 0),
    0n
  ) +
  (account.fin?.ranges?.edges || []).reduce(
    (total, edge) => total + BigInt(edge?.node?.valueUsd || 0),
    0n
  ) +
  (account.strategies || []).reduce(
    (total, strategy) =>
      total +
      BigInt(
        strategy.__typename === "%other"
          ? 0
          : strategy.__typename === "IndexAccount"
            ? strategy.sharesValue
            : strategy.valueUsd
      ),
    0n
  ) +
  (account.merge?.accounts || [])
    .filter(isUnclaimedMerge)
    .reduce((total, merge) => total + BigInt(merge.valueUsd), 0n) +
  (account.credit?.accounts || []).reduce(
    (total, credit) => total + BigInt(credit.valueUsd),
    0n
  );

// Keep account controls identical while portfolio data is loading.
const usePrivacyHeader = () => {
  const accountProvider = useWalletAccounts();
  const redacted = useRedacted();
  const privacyProvider = usePrivacyProvider();
  return {
    privateAccounts: privacyProvider.v2,
    isPrivateMode: redacted.isPrivateMode,
    activeAccount:
      redacted.isPrivateMode && redacted.subWalletAddress
        ? {
            // Model B rotates one-time addresses per action; the account is
            // shown as one wallet, never by a changing sub-wallet address.
            address:
              privacyProvider.v2 && isModelBEnabled()
                ? ""
                : redacted.subWalletAddress,
            label: "Private Account",
            icon: (
              <img
                src={privateAccountIcon}
                alt="Private account"
                aria-hidden="true"
              />
            ),
          }
        : undefined,
    privacyAction: (
      <PrivateModeToggle
        accountProvider={accountProvider}
        isPrivateMode={redacted.isPrivateMode}
        setIsPrivateMode={redacted.setIsPrivateMode}
        stealth={redacted.stealth}
        setStealth={redacted.setStealth}
        subWalletAddress={redacted.subWalletAddress}
        setSubWalletAddress={redacted.setSubWalletAddress}
        setPublicAddress={redacted.setPublicAddress}
        privacyProvider={privacyProvider}
        encryptionSignature={redacted.encryptionSignature}
        setEncryptionSignature={redacted.setEncryptionSignature}
      />
    ),
  };
};

export const HeaderContent: FC = () => {
  const v2 = useRedactedV2();
  const privacyHeader = usePrivacyHeader();
  const location = useLocation();
  const bannerChecked = useRef(false);
  const { accountData, isLoading } = usePreloadedAccountData();
  const { value: balanceValue, isLoading: balancesLoading } =
    usePortfolioBalanceValue();
  const account = useFragment<HeaderAccountFragment$key>(
    HeaderAccountFragment,
    accountData
  );
  const { showModal, showModalIfClosed, hideModal } = useGlobalModalContext();
  const [deposits, , clearDeposit, clearAllDeposits] =
    usePreloadedPendingDeposits();
  // Model B: positions live in several subs; their values are reported by
  // ModelBScope. The active one-time sub holds no positions of its own.
  const modelBPositions = privacyHeader.isPrivateMode && v2.modelB.enabled;
  const getValue = (): bigint =>
    balanceValue !== undefined
      ? (balanceValue +
          (modelBPositions
            ? v2.modelB.positionsValueUsd
            : account
              ? headerAccountValue(account)
              : 0n)) *
        10000n
      : 0n;

  const accountProvider = useWalletAccounts();
  const navigate = useNavigate();
  const { toRoot } = useLocale();
  const deposit = () => {
    // Private Mode deposits only from a connected wallet: without one the
    // dialog would be an empty list with no way to connect.
    if (privacyHeader.isPrivateMode && !accountProvider.selected) {
      navigate(toRoot("connect"));
      return;
    }
    showModal({
      title: ``,
      className: "modal--large",
      backgroundClose: false,
      // Follows the current account, which connecting a wallet in it changes.
      keepOnAccountChange: true,
      children: <Deposit />,
    });
  };
  const buyCrypto = () => {
    showModal({
      className: "modal--lg",
      backgroundClose: false,
      children: <BanxaOnRamp onBack={hideModal} />,
    });
  };

  useEffect(() => {
    if (bannerChecked.current) return;

    const returnParams = new URLSearchParams(window.location.search);
    const routerState = window.history.state?.usr;
    const isBanxaReturn =
      window.location.pathname.endsWith("/portfolio") &&
      (returnParams.has("orderId") ||
        returnParams.has("orderRef") ||
        returnParams.has("orderStatus") ||
        returnParams.has("status") ||
        routerState?.banxaReturn === true ||
        (typeof location.state === "object" &&
          location.state !== null &&
          location.state.banxaReturn === true));

    // showModal holds one modal, so a ?banxa= link must not lose it to the banner.
    const isBanxaDeepLink =
      __BANXA_ENABLED__ && !!parseBanxaDeepLink(window.location.search);

    const isAssetAction = /\/(?:deposit|withdraw|send)(?:\/|$)/.test(
      location.pathname
    );

    // Defer on routes where the banner is deliberately suppressed, instead of
    // consuming the one-time check, so it can still appear once the user leaves.
    if (isBanxaReturn || isBanxaDeepLink || isAssetAction) return;

    if (!shouldShowBanner()) {
      bannerChecked.current = true;
      return;
    }
    // A notice never replaces an open dialog (Private Mode sign-in, consent,
    // a transaction); it waits for the next route change instead.
    if (
      showModalIfClosed({
        title: ``,
        className: "modal--small",
        backgroundClose: true,
        // A notice, shown while the wallet may still be reconnecting.
        keepOnAccountChange: true,
        children: <BannerContent close={hideModal} />,
      })
    )
      bannerChecked.current = true;
  }, [hideModal, location.pathname, showModalIfClosed]);

  const routes = import.meta.env.VITE_STATIC;
  const STATIC: string[] = routes ? routes.split(",") : [];

  return (
    <BaseHeader
      accountProvider={{
        ...accountProvider,
        isLoading: isLoading || balancesLoading,
      }}
      routingElement={Link}
      ProviderIcon={ProviderIcon}
      wallets={WALLETS}
      onDeposit={deposit}
      onBuyCrypto={
        // A fiat on-ramp pays out to a public wallet after KYC.
        __BANXA_ENABLED__ && !privacyHeader.isPrivateMode
          ? buyCrypto
          : undefined
      }
      deposits={{
        deposits,
        dismissDeposit: clearDeposit,
        clearAll: clearAllDeposits,
        operation: privateHeaderOperation(v2.enabled, v2.operation),
        dismissOperation: v2.dismissOperation,
        checkOperation: v2.checkOperation,
      }}
      getAccountValue={getValue}
      {...privacyHeader}
      staticRoutes={STATIC}
    />
  );
};

const HeaderLoading: FC = () => {
  const v2 = useRedactedV2();
  const privacyHeader = usePrivacyHeader();
  const { showModal, hideModal } = useGlobalModalContext();
  const [deposits, , clearDeposit, clearAllDeposits] =
    usePreloadedPendingDeposits();

  const accountProvider = useWalletAccounts();
  const routes = import.meta.env.VITE_STATIC;
  const STATIC: string[] = routes ? routes.split(",") : [];
  const navigate = useNavigate();
  const { toRoot } = useLocale();
  const deposit = () => {
    // Private Mode deposits only from a connected wallet: without one the
    // dialog would be an empty list with no way to connect.
    if (privacyHeader.isPrivateMode && !accountProvider.selected) {
      navigate(toRoot("connect"));
      return;
    }
    showModal({
      title: ``,
      className: "modal--large",
      backgroundClose: false,
      // Follows the current account, which connecting a wallet in it changes.
      keepOnAccountChange: true,
      children: <Deposit />,
    });
  };
  const buyCrypto = () => {
    showModal({
      className: "modal--lg",
      backgroundClose: false,
      children: <BanxaOnRamp onBack={hideModal} />,
    });
  };

  return (
    <BaseHeader
      accountProvider={{ ...accountProvider, isLoading: true }}
      routingElement={Link}
      ProviderIcon={ProviderIcon}
      wallets={WALLETS}
      onDeposit={deposit}
      onBuyCrypto={
        // A fiat on-ramp pays out to a public wallet after KYC.
        __BANXA_ENABLED__ && !privacyHeader.isPrivateMode
          ? buyCrypto
          : undefined
      }
      deposits={{
        deposits,
        dismissDeposit: clearDeposit,
        clearAll: clearAllDeposits,
        operation: privateHeaderOperation(v2.enabled, v2.operation),
        dismissOperation: v2.dismissOperation,
        checkOperation: v2.checkOperation,
      }}
      getAccountValue={() => 0n}
      {...privacyHeader}
      staticRoutes={STATIC}
    />
  );
};
